import React, { useState, useEffect, useCallback, useRef } from "react";
import { connect } from "react-redux";
import { Popup } from "devextreme-react/popup";
import { Button } from "devextreme-react/button";
import { TextBox } from "devextreme-react/text-box";
import { DataGrid, Column, Paging, Pager, Scrolling } from "devextreme-react/data-grid";
import { TabPanel, Item as TabItem } from "devextreme-react/tab-panel";
import { DateBox } from "devextreme-react/date-box";
import FormField from "../../../../../dynamicFormBuilder/FormField";
import axios from "axios";
import { loadLookupFromStaticDataSource } from "../../../../../common/Utils";
import notify from "devextreme/ui/notify";

const baseUrl = () => window._env_?.REACT_APP_APIS || "";

const CUMULATIVE_DISCOUNT_FORM_SCHEMA = [
    {
        groupId: 1,
        label: "Discount",
        fields: [
            { name: "DiscountCode", tableName: "IDDISCOUNTHEADER", label: "Discount Code", type: "text", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "Retroactive", tableName: "IDDISCOUNTHEADER", label: "Retroactive", type: "text", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "BasisMethod", tableName: "IDDISCOUNTHEADER", label: "Basis Method", type: "select", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "StartingMonth", tableName: "IDDISCOUNTHEADER", label: "Start Month", type: "number", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "MonthsInPeriod", tableName: "IDDISCOUNTHEADER", label: "# of Months", type: "number", required: false, visible: true, readOnly: true, isLabelChanged: false },
        ],
    },
    {
        groupId: 2,
        label: "Apply Discounts To Orders/Cr/Adj",
        fields: [
            { name: "ApplyOnOrders", tableName: "IDDISCOUNTHEADER", label: "Open", type: "select", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "ApplyOnCommitted", tableName: "IDDISCOUNTHEADER", label: "Committed", type: "select", required: false, visible: true, readOnly: true, isLabelChanged: false },
        ],
    },
    {
        groupId: 3,
        label: "Apply To Discount Basis",
        fields: [
            { name: "IncludeMisc", tableName: "IDDISCOUNTHEADER", label: "Misc. Fees", type: "select", required: false, visible: true, readOnly: true, isLabelChanged: false },
            { name: "IncludeTaxes", tableName: "IDDISCOUNTHEADER", label: "Taxes", type: "select", required: false, visible: true, readOnly: true, isLabelChanged: false },
        ],
    },
];

function CalculateCumulativeDiscountPopup(props) {
    const { showPopup = {}, setShowPopup, setLoadPanelVisible, oidc } = props;
    const visible = showPopup.visible === true;
    const headerRowId = showPopup.headerRowId;
    const rCustomer = showPopup.rCustomer;
    const rDiscountTable = showPopup.rDiscountTable;
    const shippingDate = showPopup.shippingDate;
    const companyId = showPopup.companyId || (oidc?.user?.profile?.COMPANYID ?? null);
    const discountCode = showPopup.discountCode || "OM Cumulative Trade Discounts";

    const [discountHeader, setDiscountHeader] = useState(null);
    const [transactions, setTransactions] = useState([]);
    const [subDetails, setSubDetails] = useState([]);
    const [calcResult, setCalcResult] = useState({ OutTotalDiscountBasis: 0, OutCurrentDiscountBasis: null, OutDiscountPercentage: 0 });
    const [error, setError] = useState(null);
    const [formFields, setFormFields] = useState({
        DiscountCode: "",
        Retroactive: "",
        BasisMethod: "",
        StartingMonth: "",
        MonthsInPeriod: "",
        ApplyOnOrders: "",
        ApplyOnCommitted: "",
        IncludeMisc: "",
        IncludeTaxes: "",
        OutTotalDiscountBasis: 0,
        OutCurrentDiscountBasis: null,
        OutDiscountPercentage: 0,
    });
    const formRefs = useRef({});

    const loadData = useCallback(async () => {
        if (!visible || !companyId || !rDiscountTable) {
            if (visible && (!companyId || !rDiscountTable)) setError("Customer and Discount Table are required.");
            return;
        }
        setError(null);
        setLoadPanelVisible(true);
        const token = oidc?.user?.access_token;
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        try {
            const requestDto = { rowid: rDiscountTable };
            const transactionsRequestDto = { rCustomer, rDiscountTable };
            const [headerRes, transRes, subDetailsRes] = await Promise.all([
                axios.get(`${baseUrl()}/om/SalesOrderHeaderOperations/GetCumulativeDiscountHeader`, {
                    params: requestDto,
                    headers,
                }),
                axios.get(`${baseUrl()}/om/SalesOrderHeaderOperations/GetCumulativeDiscountTransactions`, {
                    params: transactionsRequestDto,
                    headers,
                }),
                axios.get(`${baseUrl()}/om/SalesOrderHeaderOperations/GetCumulativeDiscountSubDetails`, {
                    params: requestDto,
                    headers,
                }),
            ]);
            const header = headerRes.data || null;
            setDiscountHeader(header);
            setFormFields({
                DiscountCode: header?.DiscountCode,
                Retroactive: header?.Retroactive,
                BasisMethod: header?.BasisMethod,
                StartingMonth: header?.StartingMonth,
                MonthsInPeriod: header?.MonthsInPeriod,
                ApplyOnOrders: header?.ApplyOnOrders,
                ApplyOnCommitted: header?.ApplyOnCommitted,
                IncludeMisc: header?.IncludeMisc,
                IncludeTaxes: header?.IncludeTaxes,
            });
            setTransactions(Array.isArray(transRes.data?.Transactions) ? transRes.data.Transactions : []);
            setSubDetails(Array.isArray(subDetailsRes.data?.SubDetails)
                ? subDetailsRes.data.SubDetails.map((row, i) => ({ ...row, _key: i }))
                : []);

            if (rCustomer && rDiscountTable && headerRowId && shippingDate) {
                const calcRequestDto = {
                    rCustomer,
                    rDiscountTable,
                    currentTransRowId: headerRowId,
                    shipDate: shippingDate,
                };
                const calcRes = await axios.post(
                    `${baseUrl()}/om/SalesOrderHeaderOperations/CalculateCumulativeDiscount`,
                    calcRequestDto,
                    { headers }
                );
                const data = calcRes.data || {};
                setCalcResult({
                    OutTotalDiscountBasis: data.OutTotalDiscountBasis ?? 0,
                    OutCurrentDiscountBasis: data.OutCurrentDiscountBasis ?? null,
                    OutDiscountPercentage: data.OutDiscountPercentage ?? 0,
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to load data.");
            setDiscountHeader(null);
            setTransactions([]);
            setSubDetails([]);
        } finally {
            setLoadPanelVisible(false);
        }
    }, [visible, companyId, rCustomer, rDiscountTable, headerRowId, shippingDate, oidc?.user?.access_token, setLoadPanelVisible]);

    useEffect(() => {
        if (!visible) return;
        loadData();
    }, [visible, loadData]);

    const handleApplyDiscount = async () => {
        if (!headerRowId) return;
        setLoadPanelVisible(true);
        setError(null);
        const token = oidc?.user?.access_token;
        const applyRequestDto = { headerRowId, outDiscountPercentage: calcResult.OutDiscountPercentage ?? 0 };
        try {
            await axios.post(
                `${baseUrl()}/om/SalesOrderHeaderOperations/ApplyCumulativeDiscountToOrder`,
                applyRequestDto,
                { headers: token ? { Authorization: `Bearer ${token}` } : {} }
            );
            setShowPopup((prev) => ({ ...prev, visible: false }));
            if (props.onApplied) props.onApplied(calcResult.OutDiscountPercentage);
            notify({
                message: "Discount Applied Successfully.",
                width: 300,
            }, 'success', 3000);
        } catch (err) {
            setError(err.response?.data?.message || err.message || "Failed to apply discount.");
        } finally {
            setLoadPanelVisible(false);
        }
    };

    const handleHiding = () => {
        setShowPopup((prev) => ({ ...prev, visible: false }));
        setDiscountHeader(null);
        setTransactions([]);
        setSubDetails([]);
        setCalcResult({ OutTotalDiscountBasis: 0, OutCurrentDiscountBasis: null, OutDiscountPercentage: 0 });
        setFormFields({});
        setError(null);
    };

    const handleFieldValueChange = useCallback(() => { }, []);
    const handleFormLoad = useCallback(() => {
        loadLookupFromStaticDataSource("BasisMethod", [{ key: "T", value: "Transaction Total Only" }, { key: "S", value: "umulative Using Same Discount Code On All Transactions" }
            , { key: "A", value: "Cumulative On All Transactions With A Discount Code" }
        ], formFields, formRefs);
        loadLookupFromStaticDataSource("ApplyOnOrders", [{ key: "A", value: "All" }, { key: "N", value: "None" }], formFields, formRefs);
        loadLookupFromStaticDataSource("ApplyOnCommitted", [{ key: "A", value: "All" }, { key: "N", value: "None" }], formFields, formRefs);
        loadLookupFromStaticDataSource("IncludeMisc", [{ key: "I", value: "Include In Discount Basis" }, { key: "E", value: "Exclude From Discount Basis" }], formFields, formRefs);
        loadLookupFromStaticDataSource("IncludeTaxes", [{ key: "I", value: "Include In Discount Basis" }, { key: "E", value: "Exclude From Discount Basis" }], formFields, formRefs);

    }, []);

    const title = `OM Cumulative Trade Discounts`;

    return (
        <>
            {visible && (
                <Popup
                    visible={true}
                    onHiding={handleHiding}
                    dragEnabled={false}
                    hideOnOutsideClick={true}
                    showCloseButton={true}
                    showTitle={true}
                    title={title}
                    container="#root"
                    width="100vw"
                    height="100vh"
                >
                    <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
                        <div style={{ flex: "1 1 0", minHeight: 0, overflow: "auto" }}>
                    <FormField
                        currrentUser={oidc?.user}
                        layoutId="00000000-0000-0000-0000-000000000000"
                        layoutName="OMCumulativeDiscountPopup"
                        defaultFormSchema={CUMULATIVE_DISCOUNT_FORM_SCHEMA}
                        formFields={formFields}
                        setformFields={setFormFields}
                        handleFieldValueChange={handleFieldValueChange}
                        DYNAMICFORMSETTING="no"
                        formRefs={formRefs}
                        onFormLoad={handleFormLoad}
                        handleSelectedItemChange={() => { }}
                        handleFocusItemIn={() => { }}
                    />

                    <TabPanel>
                        <TabItem title="Transactions">
                            <DataGrid
                                dataSource={transactions}
                                keyExpr="RowId"
                                showBorders={true}
                                columnAutoWidth={true}
                                allowColumnReordering={true}
                                className="GridHeight-Popup-DynamicFormfields"
                            >
                                <Scrolling mode="standard" />
                                <Paging defaultPageSize={10} />
                                <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={[10, 25, 50]} />
                                <Column dataField="TransactionNumber" caption="Transaction #" />
                                <Column dataField="TransactionDate" caption="Transaction Date" dataType="date" format="shortDate" />
                                <Column dataField="Status" caption="Status" />
                                <Column dataField="ItemTotal" caption="Item Total" dataType="number" format={{ type: 'currency', precision: 2, currency: 'USD', useGrouping: true }} />
                                <Column dataField="DiscountableTotal" caption="Discount Basis" dataType="number" format="#,##0.00" />
                                <Column dataField="FeesAmount" caption="Fees" dataType="number" format={{ type: 'currency', precision: 2, currency: 'USD', useGrouping: true }} />
                                <Column dataField="TaxAmount" caption="Tax" dataType="number" format={{ type: 'currency', precision: 2, currency: 'USD', useGrouping: true }} />
                                <Column dataField="RecordNumber" caption="Record #" visible={false} />
                                <Column dataField="TaxableTotal" caption="Taxable" dataType="number" format={{ type: 'currency', precision: 2, currency: 'USD', useGrouping: true }} />
                                <Column dataField="Stage" caption="Stage" />
                                <Column dataField="DiscountPercent" caption="Discount" dataType="number" format="#,##0.00" />
                                <Column dataField="ShippingDate" caption="Shipping Date" dataType="date" format="shortDate" />
                            </DataGrid>
                        </TabItem>
                        <TabItem title="Discount SubDetail">
                            <DataGrid
                                dataSource={subDetails}
                                keyExpr="_key"
                                showBorders={true}
                                columnAutoWidth={true}
                                className="GridHeightAccordianOpen-tabs-with-fields"
                            >
                                <Scrolling mode="standard" />
                                <Paging defaultPageSize={10} />
                                <Pager visible={true} showPageSizeSelector={true} allowedPageSizes={[10, 25, 50]} />
                                <Column dataField="Description" caption="Description" />
                                <Column dataField="RangeTo" caption="Range To" dataType="number" format="#,##0.00" />
                                <Column dataField="DiscountPercent" caption="Discount %" dataType="number" format="#0.##'%'" />
                            </DataGrid>
                        </TabItem>
                    </TabPanel>
                        </div>
                        <div style={{ flex: "0 0 auto", borderTop: "1px solid #ddd", padding: "12px 0 0", background: "#fff", display: "flex", alignItems: "center", gap: "24px", flexWrap: "wrap" }}>
                            <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", margin: 0 }}>
                                <strong>Cumulative Discount Basis:</strong>
                                <TextBox
                                    value={`$ ${(calcResult.OutCurrentDiscountBasis ?? 0).toFixed(2)}`}
                                    readOnly
                                    stylingMode="outlined"
                                    style={{ width: "110px" }}
                                />
                            </label>
                            <label style={{ display: "inline-flex", alignItems: "center", gap: "8px", margin: 0 }}>
                                <strong>Discount %:</strong>
                                <TextBox
                                    value={`${(calcResult.OutDiscountPercentage ?? 0)}%`}
                                    readOnly
                                    stylingMode="outlined"
                                    style={{ width: "80px" }}
                                />
                            </label>
                            <div style={{ marginLeft: "auto", display: "flex", gap: "8px" }}>
                                <Button text="Apply Discount" type="default" icon="check" onClick={handleApplyDiscount} />
                                <Button text="Cancel" onClick={handleHiding} />
                            </div>
                        </div>
                    </div>
                </Popup>
            )}
        </>
    );
}

function mapStateToProps(state) {
    return {
        oidc: state.oidc,
    };
}

export default connect(mapStateToProps)(CalculateCumulativeDiscountPopup);
